import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toBnNumber } from '../../../../../common/functions';
import BreadCrumb from '../../../../../Components/BreadCrumb/BreadCrumb';
import EmptyList from '../../../../../Components/EmptyList';
import LoadingSpinner from '../../../../../Components/LoadingSpinner';
import Pagination from '../../../../../Components/Pagination/Pagination';
import LanguageContext from '../../../../../context/LanguageContext';
import StoreContext from '../../../../../context/StoreContext';
import { getBmiParticipatedListAction } from '../../../../../redux';
import ProgramHeading from '../ProgramHeading';
import BmiEditModal from './BmiEditModal';

const getBmiStatusClass = (status) => {
  const value = (status || '').toLowerCase();
  if (value.includes('severe')) return 'text-red-600 font-semibold';
  if (value.includes('moderately')) return 'text-orange-600 font-semibold';
  if (value.includes('mild mal')) return 'text-yellow-700 font-semibold';
  if (value.includes('obesity') || value.includes('overweight')) return 'text-purple-600 font-semibold';
  if (value.includes('normal')) return 'text-green-600 font-semibold';
  return 'text-gray-700';
};

const BmiParticipatedList = ({ hasShift, getParticipatedList, participatedList }) => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { classList } = useContext(StoreContext);
  const { defaultLanguage } = useContext(LanguageContext);
  const [institutionId] = useState(() => window.localStorage.getItem('institution'));
  const [gradeId, setGrade] = useState('');
  const [shiftId, setShift] = useState('');
  const [sectionId, setSection] = useState('');
  const [round, setRound] = useState('');
  const [yearId, setYearId] = useState(new Date().getFullYear());
  const [yearArray, setYearArray] = useState([]);
  const [shiftArray, setShiftArray] = useState([]);
  const [sectionArray, setSectionArray] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [pageLimit] = useState(100);
  const [rendererList, setRendererList] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBmiId, setSelectedBmiId] = useState(null);

  const displayNumber = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return defaultLanguage === 'bn' ? toBnNumber(value) : value;
  };

  const generateYearsFrom2020 = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = 2020; y <= currentYear; y++) {
      years.push({ value: y, yearName: String(y) });
    }
    return years;
  };

  useEffect(() => {
    setYearArray(generateYearsFrom2020());
  }, []);

  useEffect(() => {
    const records = participatedList?.data?.data;
    setRendererList(Array.isArray(records) ? records : []);
    setCurrentPage(1);
  }, [participatedList?.data?.data]);

  useEffect(() => {
    if (!classList?.length) return;

    const incoming = location.state || {};
    const years = generateYearsFrom2020();
    setYearArray(years);
    setYearId(incoming.academic_year || new Date().getFullYear());

    const selectedGrade = incoming.grade || classList[0]?.grade;
    const selectedClass = classList.find((item) => item.grade === Number(selectedGrade)) || classList[0];

    setGrade(selectedClass?.grade || '');

    if (hasShift?.data?.has_shift === 'yes') {
      const shifts = selectedClass?.shift_section || [];
      setShiftArray(shifts);
      setShift(incoming.shift_id || '');
      if (incoming.shift_id) {
        const selectedShift = shifts.find((item) => item.shift_id === Number(incoming.shift_id));
        setSectionArray(selectedShift?.section || []);
      } else {
        setSectionArray([]);
      }
      setSection(incoming.section_id || '');
    } else {
      const sections = selectedClass?.shift_section || [];
      setSectionArray(sections);
      setSection(incoming.section_id || '');
      setShift('');
    }

    if (incoming.round) {
      setRound(String(incoming.round));
    }
  }, [classList, hasShift?.data?.has_shift, location.state]);

  useEffect(() => {
    if (!gradeId) return;

    const data = {
      institution_id: institutionId,
      grade: gradeId,
      section_id: sectionId,
      round,
      academic_year: yearId,
      pagination_limit: pageLimit,
    };

    if (hasShift?.data?.has_shift === 'yes' && shiftId) {
      data.shift_id = shiftId;
    }

    getParticipatedList(data);
  }, [gradeId, shiftId, sectionId, round, yearId, hasShift?.data?.has_shift]);

  const handelSelectClass = (e) => {
    const value = e.target.value;
    setGrade(value);
    setShift('');
    setSection('');

    const singleClass = classList.find((classes) => classes.grade === Number(value));
    if (hasShift?.data?.has_shift === 'yes') {
      setShiftArray(singleClass?.shift_section || []);
      setSectionArray([]);
    } else {
      setSectionArray(singleClass?.shift_section || []);
    }
  };

  const handelSelectShift = (e) => {
    const value = e.target.value;
    setShift(value);
    const singleShift = shiftArray.find((shifts) => shifts.shift_id === Number(value));
    setSectionArray(singleShift?.section || []);
    setSection('');
  };

  const handelSelectSection = (e) => {
    setSection(e.target.value);
  };

  const currentTableData = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return rendererList?.slice(firstPageIndex, lastPageIndex);
  }, [pageSize, currentPage, rendererList]);

  const metaTotal = participatedList?.data?.meta?.total ?? rendererList.length;

  const refreshList = () => {
    if (!gradeId) return;

    const data = {
      institution_id: institutionId,
      grade: gradeId,
      section_id: sectionId,
      round,
      academic_year: yearId,
      pagination_limit: pageLimit,
    };

    if (hasShift?.data?.has_shift === 'yes' && shiftId) {
      data.shift_id = shiftId;
    }

    getParticipatedList(data);
  };

  const openEditModal = (student) => {
    setSelectedBmiId(student.id);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedBmiId(null);
  };

  const exportPdf = (student) => {
    navigate('/bmi/participated/pdf', { state: student });
  };

  const selectedClassName = useMemo(() => {
    const selectedClass = classList?.find((item) => item.grade === Number(gradeId));
    if (!selectedClass) return '';
    return defaultLanguage === 'en' ? selectedClass.name_en : selectedClass.name_bn;
  }, [classList, gradeId, defaultLanguage]);

  const selectedSectionName = useMemo(() => {
    const selectedSection = sectionArray?.find((item) => item.id === Number(sectionId));
    if (!selectedSection) return '';
    return defaultLanguage === 'en' ? selectedSection.section_name_en : selectedSection.section_name_bn;
  }, [sectionArray, sectionId, defaultLanguage]);

  const exportAllPdf = () => {
    navigate('/bmi/participated/list-pdf', {
      state: {
        institution_id: institutionId,
        grade: gradeId,
        shift_id: shiftId,
        section_id: sectionId,
        round,
        academic_year: yearId,
        className: selectedClassName || (gradeId ? `Class ${gradeId}` : ''),
        sectionName: selectedSectionName,
      },
    });
  };

  return (
    <React.Fragment>
      <BreadCrumb />
      <section className='mx-auto bg-white py-4 lg:py-10 space-y-4'>
        <ProgramHeading title={<FormattedMessage id='bmiParticipatedList' />} />

        <div className='container mx-auto'>
          <div className='flex flex-wrap items-center w-full px-4 sm:w-4/5 mx-auto'>
            <div className='w-full flex flex-col md:flex-row md:space-x-5 space-y-3 md:space-y-0 bg-[#399737] p-4 rounded-md'>
              <div className='w-full'>
                <label className='text-white capitalize text-[12px] lg:text-[14px]' htmlFor='class'>
                  <FormattedMessage id='class' />
                  <span className=' text-red-700'>*</span>
                </label>
                <select
                  value={gradeId}
                  onChange={handelSelectClass}
                  name='class'
                  id='class'
                  className='form-select capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-green-600 focus:outline-none'
                >
                  <option value=''>{intl.formatMessage({ id: 'selectClass' })}</option>
                  {classList?.map((classes, index) => (
                    <option key={index} value={classes.grade}>
                      {defaultLanguage === 'en' ? classes.name_en : classes.name_bn}
                    </option>
                  ))}
                </select>
              </div>

              {hasShift?.data?.has_shift === 'yes' && (
                <div className='w-full'>
                    <label className='text-white capitalize text-[12px] lg:text-[14px]' htmlFor='shift'>
                      <FormattedMessage id='shift' />
                    </label>
                  <select
                    value={shiftId}
                    onChange={handelSelectShift}
                    name='shift'
                    id='shift'
                    className='form-select capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-green-600 focus:outline-none'
                  >
                    <option value=''>{intl.formatMessage({ id: 'selectShift' })}</option>
                    {shiftArray?.map((shift, index) => (
                      <option value={shift.shift_id} key={index}>
                        {defaultLanguage === 'en' ? shift.shift_name_en : shift.shift_name_bn}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className='w-full'>
                <label className='text-white capitalize text-[12px] lg:text-[14px]' htmlFor='section'>
                  <FormattedMessage id='section' />
                </label>
                <select
                  value={sectionId}
                  onChange={handelSelectSection}
                  name='section'
                  id='section'
                  className='form-select capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-green-600 focus:outline-none'
                >
                  <option value=''>{intl.formatMessage({ id: 'selectSection' })}</option>
                  {sectionArray?.map((section, index) => (
                    <option value={section.id} key={index}>
                      {defaultLanguage === 'en' ? section.section_name_en : section.section_name_bn}
                    </option>
                  ))}
                </select>
              </div>

              <div className='w-full'>
                <label className='text-white capitalize text-[12px] lg:text-[14px]' htmlFor='year'>
                  <FormattedMessage id='year' />
                  <span className=' text-red-700'>*</span>
                </label>
                <select
                  value={yearId}
                  onChange={(e) => setYearId(e.target.value)}
                  name='year'
                  id='year'
                  className='form-select capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-green-600 focus:outline-none'
                >
                  <option value=''>{intl.formatMessage({ id: 'selectYear' })}</option>
                  {yearArray?.map((year) => (
                    <option key={year.value} value={year.value}>
                      {defaultLanguage === 'en' ? year.yearName : toBnNumber(year.yearName)}
                    </option>
                  ))}
                </select>
              </div>

              <div className='w-full'>
                <label className='text-white capitalize text-[12px] lg:text-[14px]' htmlFor='round'>
                  <FormattedMessage id='round' />
                </label>
                <select
                  value={round}
                  onChange={(e) => setRound(e.target.value)}
                  name='round'
                  id='round'
                  className='form-select capitalize block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding bg-no-repeat border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-green-600 focus:outline-none'
                >
                  <option value=''>{intl.formatMessage({ id: 'selectRound' })}</option>
                  <option value='1'>
                    {intl.formatMessage({ id: 'round' })} {defaultLanguage === 'en' ? 1 : toBnNumber(1)}
                  </option>
                  <option value='2'>
                    {intl.formatMessage({ id: 'round' })} {defaultLanguage === 'en' ? 2 : toBnNumber(2)}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className='container mx-auto'>
          <div className='flex flex-wrap flex-col w-full px-4 sm:w-4/5 mx-auto'>
            <div className='border border-gray-300 w-full rounded overflow-x-auto'>
              <div className='flex flex-wrap items-center justify-between w-full gap-3 border-b border-gray-300 px-4 py-2'>
                <p className='text-base text-black capitalize'>
                  <FormattedMessage id='totalStudent' /> : <span>{displayNumber(metaTotal)}</span>
                </p>

                <button
                  type='button'
                  onClick={exportAllPdf}
                  disabled={!rendererList.length}
                  className='py-1.5 px-4 rounded bg-[#399737] text-white capitalize text-sm hover:opacity-90 disabled:opacity-50'
                >
                  <FormattedMessage id='export' />
                </button>
              </div>

              {participatedList?.loading ? (
                <LoadingSpinner />
              ) : currentTableData && currentTableData.length ? (
                <table className='w-full border-collapse border border-gray-300 min-w-[700px]'>
                  <thead className='text-left bg-[#BFE3B5]'>
                    <tr>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='rollNumber' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='studentName' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='gender' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='age' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='round' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='bmi' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='bmiType' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='date' />
                      </th>
                      <th className='border border-gray-300 capitalize p-2'>
                        <FormattedMessage id='action' />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTableData.map((student) => (
                      <tr className='hover:bg-green-50' key={student.id}>
                        <td className='border border-gray-300 p-2'>{displayNumber(student.roll)}</td>
                        <td className='border border-gray-300 p-2 capitalize'>{student.name}</td>
                        <td className='border border-gray-300 p-2 capitalize'>
                          {student.gender ? <FormattedMessage id={student.gender} defaultMessage={student.gender} /> : '-'}
                        </td>
                        <td className='border border-gray-300 p-2'>{displayNumber(student.age)}</td>
                        <td className='border border-gray-300 p-2'>{displayNumber(student.round)}</td>
                        <td className='border border-gray-300 p-2'>{displayNumber(student.bmi)}</td>
                        <td className={`border border-gray-300 p-2 ${getBmiStatusClass(student.bmi_status)}`}>
                          {student.bmi_status || '-'}
                        </td>
                        <td className='border border-gray-300 p-2'>{displayNumber(student.date)}</td>
                        <td className='border border-gray-300 p-2 space-x-3'>
                          <button
                            type='button'
                            onClick={() => openEditModal(student)}
                            className='text-green-600 hover:text-green-800 capitalize font-medium'
                          >
                            <FormattedMessage id='edit' />
                          </button>
                          <button
                            type='button'
                            onClick={() => exportPdf(student)}
                            className='text-green-600 hover:text-green-800 capitalize font-medium'
                          >
                            <FormattedMessage id='export' />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <EmptyList message={intl.formatMessage({ id: 'emptyBmiParticipatedList' })} border={false} />
              )}
            </div>

            <div className='container mx-auto my-5'>
              <div className='w-full'>
                <Pagination
                  className='pagination-bar'
                  currentPage={currentPage}
                  totalCount={rendererList?.length || 0}
                  pageSize={pageSize}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {showEditModal && selectedBmiId && (
        <BmiEditModal bmiId={selectedBmiId} onClose={closeEditModal} onSuccess={refreshList} />
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    hasShift: state.login?.currentUser,
    participatedList: state.bmiParticipatedList,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getParticipatedList: (studentInfo) => dispatch(getBmiParticipatedListAction(studentInfo)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BmiParticipatedList);
